import {
  Document, Packer, Paragraph, TextRun,
  Table, TableRow, TableCell, WidthType,
  Header, Footer,
  AlignmentType, HeadingLevel, BorderStyle,
} from 'docx';
import { saveAs } from 'file-saver';

function stripMeta(content) {
  return content
    .replace(/\nCEREBRO_JSON:[^\n]*/g, '')
    .replace(/^CEREBRO_JSON:[^\n]*/gm, '')
    .trim();
}

function parseInlineBold(line) {
  const parts = line.split(/\*\*([^*]+)\*\*/g);
  return parts.map((part, i) =>
    new TextRun({ text: part, bold: i % 2 === 1, size: 22, color: '1F2937' })
  );
}

function parseMarkdownTable(tableLines) {
  const dataRows = tableLines.filter(l => !/^\|[-| :]+\|/.test(l.trim()));
  const rows = dataRows.map(l =>
    l.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim())
  );
  const colCount = Math.max(...rows.map(r => r.length));
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: rows.map((row, ri) => new TableRow({
      children: Array.from({ length: colCount }, (_, ci) => new TableCell({
        children: [new Paragraph({
          children: [new TextRun({ text: row[ci] || '', bold: ri === 0, size: 20, color: ri === 0 ? '111827' : '374151' })],
          spacing: { before: 60, after: 60 },
        })],
        margins: { top: 80, bottom: 80, left: 120, right: 120 },
        shading: ri === 0 ? { fill: 'EEF2FF' } : undefined,
      })),
    })),
  });
}

function parseLines(content) {
  const paragraphs = [];
  const lines = content.split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trimEnd();
    if (/^\|.+\|/.test(line)) {
      const tableLines = [];
      while (i < lines.length && /^\|/.test(lines[i].trimEnd())) {
        tableLines.push(lines[i].trimEnd());
        i++;
      }
      paragraphs.push(new Paragraph({ text: '', spacing: { after: 80 } }));
      paragraphs.push(parseMarkdownTable(tableLines));
      paragraphs.push(new Paragraph({ text: '', spacing: { after: 120 } }));
      continue;
    }
    if (!line) {
      paragraphs.push(new Paragraph({ text: '', spacing: { after: 60 } }));
    } else if (/^#{1,2}\s/.test(line)) {
      const lvl = line.startsWith('## ') ? HeadingLevel.HEADING_2 : HeadingLevel.HEADING_1;
      paragraphs.push(new Paragraph({
        text: line.replace(/^#+\s/, ''),
        heading: lvl,
        spacing: { before: 240, after: 100 },
      }));
    } else if (/^[-•*]\s/.test(line)) {
      paragraphs.push(new Paragraph({
        children: parseInlineBold(line.replace(/^[-•*]\s/, '')),
        bullet: { level: 0 },
        spacing: { after: 60 },
      }));
    } else if (line.startsWith('> ')) {
      paragraphs.push(new Paragraph({
        children: [new TextRun({ text: line.slice(2), italics: true, color: '6B7280', size: 21 })],
        spacing: { after: 80 },
        indent: { left: 720 },
      }));
    } else {
      paragraphs.push(new Paragraph({
        children: parseInlineBold(line),
        spacing: { after: 80 },
      }));
    }
    i++;
  }
  return paragraphs;
}

export async function exportChatToDocx(messages, clientName = 'Marca') {
  const date = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
  const safeName = clientName.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_');

  const headerChildren = [
    new Paragraph({
      children: [
        new TextRun({ text: '✦ ', color: '6366F1', size: 40, bold: true }),
        new TextRun({ text: 'GORMARAN', color: '6366F1', size: 40, bold: true, characterSpacing: 300 }),
        new TextRun({ text: '.IO', color: 'A5B4FC', size: 40, bold: true }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: 80, after: 40 },
    }),
  ];

  const watermarkHeader = new Header({ children: headerChildren });

  const brandFooter = new Footer({
    children: [
      new Paragraph({
        children: [
          new TextRun({ text: 'Generado por ', color: '9CA3AF', size: 18 }),
          new TextRun({ text: 'Gormaran.io', color: '6366F1', size: 18, bold: true }),
          new TextRun({ text: `  ·  Cerebro de Marca: ${clientName}  ·  ${date}`, color: '9CA3AF', size: 18 }),
        ],
        alignment: AlignmentType.CENTER,
        border: { top: { style: BorderStyle.SINGLE, size: 4, color: 'E5E7EB' } },
        spacing: { before: 60 },
      }),
    ],
  });

  const body = [];

  // Cover block
  body.push(
    new Paragraph({
      children: [new TextRun({ text: `Cerebro de Marca`, size: 36, color: '6366F1', bold: true })],
      alignment: AlignmentType.LEFT,
      spacing: { after: 80 },
    }),
    new Paragraph({
      children: [new TextRun({ text: clientName, size: 56, bold: true, color: '111827' })],
      spacing: { after: 80 },
    }),
    new Paragraph({
      children: [
        new TextRun({ text: `Generado el ${date}  ·  `, color: '6B7280', size: 20 }),
        new TextRun({ text: 'Gormaran.io', color: '6366F1', size: 20, bold: true }),
      ],
      spacing: { after: 600 },
    }),
  );

  // Messages
  const display = messages
    .filter(m => m.role === 'assistant' || (m.role === 'user' && !/^💾/.test(m.content.trim())))
    .map(m => ({ ...m, content: stripMeta(m.content) }))
    .filter(m => m.content.length > 0);

  display.forEach(m => {
    if (m.role === 'user') {
      body.push(
        new Paragraph({
          children: [
            new TextRun({ text: '▶ ', color: '6366F1', bold: true, size: 22 }),
            new TextRun({ text: m.content, bold: true, size: 22, color: '374151' }),
          ],
          spacing: { before: 360, after: 120 },
          shading: undefined,
        })
      );
    } else {
      body.push(...parseLines(m.content));
    }
  });

  // End watermark stamp
  body.push(
    new Paragraph({ text: '', spacing: { before: 800 } }),
    new Paragraph({
      children: [
        new TextRun({ text: '— Informe generado por Gormaran.io · IA para Agencias de Marketing —', color: 'A5B4FC', size: 18, italics: true }),
      ],
      alignment: AlignmentType.CENTER,
      border: {
        top: { style: BorderStyle.SINGLE, size: 4, color: 'E5E7EB' },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: 'E5E7EB' },
      },
      spacing: { before: 160, after: 160 },
    }),
  );

  const doc = new Document({
    creator: 'Gormaran.io',
    company: 'Gormaran Marketing AI',
    title: `Cerebro de Marca — ${clientName}`,
    description: 'Informe generado por Gormaran.io — IA para Agencias de Marketing',
    sections: [{
      headers: { default: watermarkHeader },
      footers: { default: brandFooter },
      children: body,
    }],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `CerebroMarca_${safeName}_Gormaran.docx`);
}
